<script lang="ts">
  import type { Snippet } from "svelte";
  import CheckBold from "~icons/ph/check-bold"
  import MagnifyingGlass from "~icons/ph/magnifying-glass"

  let {
    options,
    height = "h-56",
    inputValue = $bindable(""),
    allowNew = true,
    noResults,
    required = false,
    ...props
  }: {
    options: string[];
    height?: string;
    inputValue?: string;
    allowNew?: boolean;
    noResults?: Snippet;
    required?: boolean;
    [key: string]: any;
  } = $props()

  let input = $state<HTMLInputElement>()
  let dropdown = $state<HTMLDivElement>()
  let newValueSelected = $state<boolean>(false)
  const optClasses = "w-full text-left px-4 py-2 hover:bg-base-200 focus:bg-base-200 outline-none transition-colors duration-150 data-[selected]:bg-info-200"

  let shownOpts = $derived.by(() => {
    let lowerInput = inputValue.toLowerCase().trim()
    let sortOpts = options.flatMap((opt: string) => {
      let lowerOpt = opt.toLowerCase()
      if (!lowerOpt.includes(lowerInput)) return [];
      return {
        opt,
        startsWith: lowerOpt.startsWith(lowerInput) ? 1 : -1,
        lowerOpt,
      };
    })

    return sortOpts
      .sort((a, b) => b.startsWith - a.startsWith || a.lowerOpt.localeCompare(b.lowerOpt))
      .map(s => s.opt)
  });

  let exactMatchExists = $derived(shownOpts.map(opt => opt.toLowerCase()).includes(inputValue.trim().toLowerCase()));

  const selectOpt = (value: string) => {
    newValueSelected = (value == inputValue.trim()) ? true : false;
    inputValue = value;
    input?.focus();
  };

  let focusable = $derived.by(() => {
    shownOpts; // track this so focusable recalculates when options change
    return [input, ...Array.from(dropdown?.children || [])] as HTMLElement[]
  })

  const arrownav = (e: KeyboardEvent) => {
    let focus = (i: number) => {
      let index = focusable.indexOf(e.currentTarget as HTMLElement)
      focusable[index + i]?.focus()
      e.preventDefault()
    }

    if(e.key == "ArrowUp") { return focus(-1) }
    if(e.key == "ArrowDown") { return focus(1) }
  }

  const buttonKeydown = (e: KeyboardEvent) => {
    if(e.key.length === 1 || e.key === "Backspace") {
      input?.focus();
      e.preventDefault()
      if(e.key.length === 1) inputValue += e.key
      if(e.key == "Backspace") inputValue = inputValue.slice(0, -1)
      return
    }
    arrownav(e)
  }

  const isSelected = (opt: string) => opt.trim().toLowerCase() === inputValue.trim().toLowerCase() || undefined

  $effect(() => {
    if (!input) return;
    const valueInvalid = required && !allowNew && inputValue.trim() !== "" && !exactMatchExists;
    input.setCustomValidity(valueInvalid ? "Please select an item from the list" : "");
  });
</script>

<div class="w-full {height} join join-vertical">
  <label class="ui-input join-item flex items-center gap-2 flex-none focus-within:outline-none focus-within:ring-1 focus-within:ring-primary focus-within:ring-inset">
    <MagnifyingGlass class="w-5 h-5 text-base-content/50"/>
    <input
      bind:this={input}
      bind:value={inputValue}
      type="text"
      list=""
      autocomplete="off"
      onkeydown={arrownav}
      class="grow focus:outline-none"
      tabindex={0}
      {required}
      {...props}
    />
  </label>

  {#if shownOpts.length > 0 || inputValue.trim()}
    <div bind:this={dropdown} class="join-item w-full bg-base-100 border border-t-1 mt-[-1px] border-base-300 rounded-b-lg overflow-y-auto">
      {#each shownOpts as opt}
        {@const selected = isSelected(opt)}
        <button type="button" onclick={() => selectOpt(opt)} class={optClasses} data-selected={selected}
          data-value={opt} tabindex={0} onkeydown={buttonKeydown}>

          {#if selected}
            <CheckBold class="w-4 h-4 inline-block mr-2" />
          {/if}
          {opt}
        </button>
      {/each}

      {#if inputValue.trim() && !exactMatchExists && allowNew}
        <button type="button" onclick={() => selectOpt(inputValue.trim())} class={optClasses} data-selected={newValueSelected}
          data-value={inputValue} tabindex={0} onkeydown={buttonKeydown}>

          {#if newValueSelected}
            <CheckBold class="w-4 h-4 inline-block mr-2" />
          {/if}
          <b>Use "{inputValue.trim()}"</b>
        </button>
      {:else if inputValue.trim() && shownOpts.length === 0 && !allowNew}
        {#if noResults}
          {@render noResults()}
        {:else}
          <div class="px-4 py-2 text-gray-500">
            No matching results
          </div>
        {/if}
      {/if}
    </div>
  {/if}
</div>
